import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import MenuItem from "@mui/material/MenuItem";
import Grid from "@mui/material/Grid";
import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Container from "@mui/material/Container";
import ShoppingCartCheckoutIcon from "@mui/icons-material/ShoppingCartCheckout";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { useCart } from "../../../Context/CartContext";
import { resolveImageUrl } from "../../../services/indexMediaBaseUrl";
import { getProductInfo, getCountriesWithShippingCategories } from "../../../services/Public/PublicPagesApi"; // used for fetching missing product details and countries/shipping
import { useTranslation } from "react-i18next";
import PaymentMethods from "./PaymentMethods";
import { easykashPayment } from "../../../services/UserApi/CartApi";
import CircularProgress from "@mui/material/CircularProgress";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";


export default function MakeOrderPage({ products: propsProducts }) {
  const { t, i18n } = useTranslation("make_order_page");
  let lang = (i18n && i18n.language) || localStorage.getItem("lang") || "ar";
  if (lang === "rn") lang = "en";
  lang = lang.startsWith("ar") ? "ar" : "en";
  const getName = (p) => (p ? (lang === "ar" ? (p.name_ar || p.name_en) : (p.name_en || p.name_ar)) : "");
   useEffect(() => {
    document.title = 'DIDA MAX — Order Review';
  }, []);
  const location = useLocation();
  const navigate = useNavigate();
  const { makeOrder, cart } = useCart();

  // rawItems: prefer items passed via navigate state (CartDrawer), else cart context
  const rawItems =
    propsProducts ||
    location.state?.items ||
    cart?.data?.items ||
    [];

  // normalized entries: keep item_id, product_id, qty, price, product (may be undefined), image (may be provided)
  const normalize = (it) => {
    const product = it.product || {};
    const rawPhoto = it.image || it.product?.main_photo || product?.main_photo || product?.images?.[0]?.image || it.image || it.photo || "";
    // accept size/color passed from CartDrawer (object or id) or fallback to product-level arrays
    const sizeObj = it.size || (it.size_id && Array.isArray(product.sizes)
      ? product.sizes.find((s) => String(s.id) === String(it.size_id))
      : null);
    const colorObj = it.color || (it.color_id && Array.isArray(product.colors)
      ? product.colors.find((c) => String(c.id) === String(it.color_id))
      : null);
    const sizeLabel = sizeObj ? (sizeObj.size || sizeObj.key || String(sizeObj.id)) : (it.size_id ? String(it.size_id) : "");
    const colorLabel = colorObj ? (colorObj.color_en || colorObj.color_ar || String(colorObj.id)) : (it.color_id ? String(it.color_id) : "");

    return {
      item_id: it.item_id ?? it.id,
      product_id: it.product_id ?? product?.id,
      quantity: it.quantity ?? it.qty ?? 1,
      price: it.price ?? product?.currencies?.[0]?.price ?? 0,
      product,
      image: rawPhoto || undefined, // may be resolved later
      size_id: it.size_id ?? sizeObj?.id ?? null,
      color_id: it.color_id ?? colorObj?.id ?? null,
      size: sizeObj || null,
      color: colorObj || null,
      sizeLabel,
      colorLabel,
      raw: it,
    };
  };

  const [items, setItems] = useState((Array.isArray(rawItems) ? rawItems : []).map(normalize));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null); // null | 'pending' | 'opened'
  const [form, setForm] = useState({
    payment_method: "cash",
    payment_provider: "",
    phone: "",
    country_id: "",
    governorate_id: "",
    city_id: "",
    address: "",
    postal_code: "",
    notes: "",
    shipping_cost: "",
    discount: "",
  });

  // handler to receive updates from PaymentMethods
  const handlePaymentChange = (v) => {
    setForm(s => ({ ...s, ...(v.payment_method !== undefined ? { payment_method: v.payment_method } : {}), ...(v.payment_provider !== undefined ? { payment_provider: v.payment_provider } : {}) }));
  };

  // --- NEW state for countries / governorates / cities
  const [countries, setCountries] = useState([]);
  const [availableGovernorates, setAvailableGovernorates] = useState([]);
  const [availableCities, setAvailableCities] = useState([]);

  // allow manual city input when not present in list
  const [isManualCity, setIsManualCity] = useState(false);
  const [cityInput, setCityInput] = useState("");

  const handleChange = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  // helper: extract unique governorates from country.categories
  const extractGovernorates = (country) => {
    // return governorates with attached _shipping_category { id, cost }
    const map = new Map();
    (country?.categories || []).forEach((cat) => {
      const catCost = cat?.cost ?? "";
      (cat.governorates || []).forEach((g) => {
        if (!map.has(g.id)) {
          map.set(g.id, { ...g, _shipping_category: { id: cat.id, cost: catCost } });
        } else {
          // prefer a non-empty cost if available
          const existing = map.get(g.id);
          if ((!existing._shipping_category || !existing._shipping_category.cost) && catCost) {
            map.set(g.id, { ...g, _shipping_category: { id: cat.id, cost: catCost } });
          }
        }
      });
    });
    return Array.from(map.values());
  };

  const shippingCostFromGovernorate = (gov) => {
    if (!gov) return "";
    const cost = gov._shipping_category?.cost ?? "";
    return cost !== null && cost !== undefined ? String(cost) : "";
  };

  // fetch countries + shipping categories on mount
  useEffect(() => {
  let mounted = true;

  (async () => {
    try {
      const res = await getCountriesWithShippingCategories();

      // ✅ لأن الدالة بترجع response.data مباشرة
      const data = res?.countries || res?.data || res || [];
      const list = Array.isArray(data) ? data : [];

      console.log("COUNTRIES RES:", res);
      console.log("COUNTRIES LIST:", list);

      if (!mounted) return;

      setCountries(list);

      if (list.length > 0) {
        // prefer savedCountryId from localStorage, then current form.country_id, else first country
        const preferredId = savedCountryId || form.country_id;
        const initialCountry =
          list.find((c) => String(c.id) === String(preferredId)) || list[0];

        const govs = extractGovernorates(initialCountry);
        setAvailableGovernorates(govs);

        if (govs.length > 0) {
          const initialGov =
            govs.find((g) => String(g.id) === String(form.governorate_id)) || govs[0];

          setAvailableCities(initialGov.cities || []);
          setForm((s) => ({
            ...s,
            country_id: s.country_id || String(initialCountry.id),
            governorate_id: s.governorate_id || String(initialGov.id),
            city_id:
              s.city_id ||
              (initialGov.cities && initialGov.cities[0] ? String(initialGov.cities[0].id) : ""),
            shipping_cost: shippingCostFromGovernorate(initialGov) || s.shipping_cost || "",
          }));
        } else {
          setForm((s) => ({
            ...s,
            country_id: s.country_id || String(initialCountry.id),
          }));
        }
      }
    } catch (err) {
      console.error("getCountriesWithShippingCategories error", err);
    }
  })();

  return () => {
    mounted = false;
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);


  // handlers for dependent selects
  const handleCountrySelect = (e) => {
    const val = e.target.value;
    setForm(s => ({ ...s, country_id: val, governorate_id: "", city_id: "", shipping_cost: "" }));
    const country = countries.find(c => String(c.id) === String(val));
    const govs = country ? extractGovernorates(country) : [];
    setAvailableGovernorates(govs);
    setAvailableCities([]);
    if (govs.length > 0) {
      const firstGov = govs[0];
      setForm(s => ({ ...s, governorate_id: String(firstGov.id), city_id: firstGov.cities && firstGov.cities[0] ? String(firstGov.cities[0].id) : "", shipping_cost: shippingCostFromGovernorate(firstGov) || "" }));
      setAvailableCities(firstGov.cities || []);
    }
  };

  const handleGovernorateSelect = (e) => {
    const val = e.target.value;
    setForm(s => ({ ...s, governorate_id: val, city_id: "", shipping_cost: "" }));
    setIsManualCity(false);
    setCityInput("");
    const gov = availableGovernorates.find(g => String(g.id) === String(val));
    const cities = gov?.cities || [];
    setAvailableCities(cities);
    const cost = shippingCostFromGovernorate(gov);
    if (cities.length > 0) setForm(s => ({ ...s, city_id: String(cities[0].id), shipping_cost: cost || s.shipping_cost || "" }));
    else setForm(s => ({ ...s, shipping_cost: cost || s.shipping_cost || "" }));
  };

  const handleSubmit = async (e) => {
  e.preventDefault();
  setError("");
  setSuccessMsg("");
  setPaymentStatus(null);
  setLoading(true);

  try {
    const requiredByApi = ["payment_method", "phone", "country_id", "governorate_id", "address"];
    for (const k of requiredByApi) {
      const v = form[k];
      if (v === null || v === undefined || String(v).trim() === "") {
        throw new Error(t('validation.required_field', { field: k }));
      }
    }

    const payload = {
      payment_method: form.payment_method || "cash",
      phone: form.phone || undefined,
      country_id: form.country_id ? Number(form.country_id) : undefined,
      governorate_id: form.governorate_id ? Number(form.governorate_id) : undefined,
      address: form.address || undefined,
      postal_code: form.postal_code || undefined,
      notes: form.notes || undefined,
      shipping_cost: form.shipping_cost ? Number(form.shipping_cost) : undefined,
      discount: form.discount ? Number(form.discount) : undefined,
    };

    if (isManualCity) {
      if (cityInput && String(cityInput).trim() !== "") payload.city_name = cityInput.trim();
    } else if (form.city_id) {
      payload.city_id = Number(form.city_id);
    }
    if (form.payment_provider) payload.payment_provider = form.payment_provider;

    const orderRes = await makeOrder(payload);
    const orderId = orderRes?.order_id;
    const paymentType = orderRes?.payment_type;

    // cash → navigate directly
    if (paymentType === "cash" || !orderId) {
      setSuccessMsg(lang === "ar" ? "تم إنشاء الطلب بنجاح" : "Order created successfully");
      navigate("/myorders");
      return;
    }

    // online payment → call easykash
    setPaymentLoading(true);
    setPaymentStatus("pending");

    const payRes = await easykashPayment({ order_id: orderId });

    if (payRes?.success && payRes?.payment_url) {
      localStorage.setItem("pending_payment_id", String(payRes.payment_id ?? ""));
      localStorage.setItem("pending_payment_url", String(payRes.payment_url));
      localStorage.setItem("pending_order_id", String(orderId));
      navigate("/order-payment", {
        state: {
          payment_id:  payRes.payment_id,
          payment_url: payRes.payment_url,
          order_id:    orderId,
        },
        replace: true,
      });
      return;
    } else {
      setPaymentStatus(null);
      setError(
        lang === "ar"
          ? "تم إنشاء الطلب لكن فشل تهيئة الدفع. يرجى المحاولة لاحقاً."
          : "Order created but payment setup failed. Please try again later."
      );
    }
  } catch (err) {
    console.log("MAKE ORDER ERROR:", err?.response?.status, err?.response?.data);

    const apiData = err?.response?.data;
    const errorsObj = apiData?.errors;

    const msg =
      (errorsObj && Object.values(errorsObj).flat().join("\n")) ||
      apiData?.message_ar ||
      apiData?.message ||
      err?.message ||
      t('order.create_fail');

    setError(msg);
    setPaymentStatus(null);
  } finally {
    setLoading(false);
    setPaymentLoading(false);
  }
};


  const subtotal = items.reduce((s, it) => s + Number(it.price || 0) * Number(it.quantity || 1), 0);
  const shippingCost = Number(form.shipping_cost) || 0;
  const total = subtotal + shippingCost;

  const SectionHeader = ({ icon, label, step }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: "50%",
          background: "linear-gradient(135deg, #d5aa2f 0%, #e6c76a 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          boxShadow: "0 4px 12px rgba(213,170,47,0.35)",
        }}
      >
        <Typography variant="caption" fontWeight={900} color="#fff" sx={{ fontSize: "0.85rem" }}>
          {step}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {icon}
        <Typography variant="h6" fontWeight={800} color="text.primary">
          {label}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#f8f9fa",
        py: { xs: 3, md: 5 },
      }}
    >
      {/* Top accent bar */}
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: "linear-gradient(90deg, #d5aa2f 0%, #e6c76a 50%, #d5aa2f 100%)",
          zIndex: 1200,
        }}
      />

      <Container maxWidth="xl">
        {/* Page Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 3,
                background: "linear-gradient(135deg, #d5aa2f 0%, #e6c76a 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 8px 24px rgba(213,170,47,0.3)",
              }}
            >
              <ShoppingCartCheckoutIcon sx={{ color: "#fff", fontSize: 26 }} />
            </Box>
            <Box>
              <Typography variant="h4" fontWeight={900} color="text.primary" lineHeight={1.2}>
                {t('order.review_items')}
              </Typography>
              <Typography variant="body2" color="text.secondary" fontWeight={600}>
                {items.length} {t('products_in_cart', { defaultValue: 'منتج في سلتك' })}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Two-column layout */}
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            display: "flex",
            gap: 3,
            flexDirection: { xs: "column", lg: "row" },
            alignItems: "flex-start",
          }}
        >
          {/* ── LEFT: Form ── */}
          <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 3 }}>

            {/* Step 1 — Payment */}
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2.5, md: 3.5 },
                borderRadius: 4,
                border: "1px solid",
                borderColor: "grey.100",
                boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
                bgcolor: "#fff",
              }}
            >
              <SectionHeader
                step="١"
                icon={<ShoppingCartCheckoutIcon sx={{ color: "primary.main", fontSize: 22 }} />}
                label={t('payment_method', { defaultValue: 'طريقة الدفع' })}
              />
              <PaymentMethods
                value={form.payment_method}
                provider={form.payment_provider}
                onChange={handlePaymentChange}
              />
            </Paper>

            {/* Step 2 — Shipping */}
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2.5, md: 3.5 },
                borderRadius: 4,
                border: "1px solid",
                borderColor: "grey.100",
                boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
                bgcolor: "#fff",
              }}
            >
              <SectionHeader
                step="٢"
                icon={<LocalShippingIcon sx={{ color: "primary.main", fontSize: 22 }} />}
                label={t('shipping_info', { defaultValue: 'معلومات الشحن' })}
              />
              <Grid container spacing={2.5}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={t('phone')}
                    value={form.phone}
                    onChange={handleChange("phone")}
                    required
                    fullWidth
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2.5 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    label={t('country')}
                    value={form.country_id}
                    onChange={handleCountrySelect}
                    disabled
                    fullWidth
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2.5 } }}
                  >
                    {countries.length === 0 ? (
                      <MenuItem value="">{t('loading')}</MenuItem>
                    ) : (
                      countries.map((c) => (
                        <MenuItem key={c.id} value={String(c.id)}>
                          {c.name_ar || c.name_en || `#${c.id}`}
                        </MenuItem>
                      ))
                    )}
                  </TextField>
                </Grid>
              </Grid>
            </Paper>

            {/* Step 3 — Address */}
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2.5, md: 3.5 },
                borderRadius: 4,
                border: "1px solid",
                borderColor: "grey.100",
                boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
                bgcolor: "#fff",
              }}
            >
              <SectionHeader
                step="٣"
                icon={<LocationOnIcon sx={{ color: "primary.main", fontSize: 22 }} />}
                label={t('address_details', { defaultValue: 'تفاصيل العنوان' })}
              />
              <Grid container spacing={2.5}>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    label={t('governorate')}
                    value={form.governorate_id}
                    onChange={handleGovernorateSelect}
                    fullWidth
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2.5 } }}
                  >
                    {availableGovernorates.length === 0 ? (
                      <MenuItem value="">{t('no_governorates')}</MenuItem>
                    ) : (
                      availableGovernorates.map((g) => (
                        <MenuItem key={g.id} value={String(g.id)}>
                          {g.name_ar || g.name_en || `#${g.id}`}
                        </MenuItem>
                      ))
                    )}
                  </TextField>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    label={t('city')}
                    value={isManualCity ? "__manual__" : form.city_id}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === "__manual__") {
                        setIsManualCity(true);
                        setForm((s) => ({ ...s, city_id: "" }));
                        setCityInput("");
                      } else {
                        setIsManualCity(false);
                        setCityInput("");
                        setForm((s) => ({ ...s, city_id: v }));
                      }
                    }}
                    fullWidth
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2.5 } }}
                  >
                    {availableCities.length === 0 ? (
                      <MenuItem value="">{t('no_cities')}</MenuItem>
                    ) : (
                      availableCities.map((c) => (
                        <MenuItem key={c.id} value={String(c.id)}>
                          {c.name_ar || c.name_en || `#${c.id}`}
                        </MenuItem>
                      ))
                    )}
                    <MenuItem value="__manual__">
                      {t('other_city', { defaultValue: 'أخرى / أدخل المدينة' })}
                    </MenuItem>
                  </TextField>
                </Grid>

                {isManualCity && (
                  <Grid item xs={12}>
                    <TextField
                      label={t('city_name', { defaultValue: 'اسم المدينة' })}
                      value={cityInput}
                      onChange={(e) => setCityInput(e.target.value)}
                      helperText={t('city_manual_helper', { defaultValue: 'اكتب اسم المدينة وسيتم إرساله كسلسلة' })}
                      fullWidth
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2.5 } }}
                    />
                  </Grid>
                )}

                <Grid item xs={12}>
                  <TextField
                    label={t('address')}
                    value={form.address}
                    onChange={handleChange("address")}
                    multiline
                    minRows={3}
                    fullWidth
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2.5 } }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label={t('postal_code')}
                    value={form.postal_code}
                    onChange={handleChange("postal_code")}
                    fullWidth
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2.5 } }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label={t('shipping_cost')}
                    value={form.shipping_cost}
                    InputProps={{ readOnly: true }}
                    disabled
                    fullWidth
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2.5 } }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label={t('notes')}
                    value={form.notes}
                    onChange={handleChange("notes")}
                    multiline
                    minRows={2}
                    fullWidth
                    placeholder={t('notes_placeholder', { defaultValue: 'أي ملاحظات إضافية...' })}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2.5 } }}
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Alerts */}
            {error && (
              <Alert severity="error" icon={<ErrorOutlineIcon />} sx={{ borderRadius: 3, fontWeight: 600 }}>
                {error}
              </Alert>
            )}
            {successMsg && (
              <Alert severity="success" icon={<CheckCircleOutlineIcon />} sx={{ borderRadius: 3, fontWeight: 600 }}>
                {successMsg}
              </Alert>
            )}
            {paymentStatus === "pending" && !error && (
              <Alert severity="info" icon={<CircularProgress size={20} />} sx={{ borderRadius: 3, fontWeight: 600 }}>
                {lang === "ar" ? "جارٍ تهيئة صفحة الدفع..." : "Preparing payment page..."}
              </Alert>
            )}

            {/* Submit Buttons (mobile) — also appears in sidebar on desktop */}
            <Box sx={{ display: { xs: "flex", lg: "none" }, gap: 2 }}>
              <Button
                type="submit"
                variant="contained"
                disabled={loading || paymentLoading || items.length === 0}
                size="large"
                fullWidth
                sx={{
                  py: 1.8,
                  fontSize: "1rem",
                  fontWeight: 800,
                  borderRadius: 3,
                  textTransform: "none",
                  background: "linear-gradient(135deg, #d5aa2f 0%, #e6c76a 100%)",
                  boxShadow: "0 4px 16px rgba(213,170,47,0.35)",
                  "&:hover": { background: "linear-gradient(135deg, #b08c26 0%, #d5aa2f 100%)" },
                  "&:disabled": { background: "linear-gradient(135deg, #e0e0e0 0%, #bdbdbd 100%)" },
                }}
              >
                {loading || paymentLoading
                  ? (paymentStatus === "pending" ? (lang === "ar" ? "جارٍ تهيئة الدفع..." : "Setting up payment...") : t('loading'))
                  : t('order.create')}
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate(-1)}
                size="large"
                sx={{ py: 1.8, fontWeight: 700, borderRadius: 3, textTransform: "none", borderWidth: 2, flexShrink: 0, px: 3, "&:hover": { borderWidth: 2 } }}
              >
                {t('cancel')}
              </Button>
            </Box>
          </Box>

          {/* ── RIGHT: Order Summary ── */}
          <Box
            sx={{
              width: { lg: 360 },
              flexShrink: 0,
              position: { lg: "sticky" },
              top: 24,
              alignSelf: "flex-start",
              display: "flex",
              flexDirection: "column",
              gap: 3,
            }}
          >
            {/* Products Summary */}
            <Paper
              elevation={0}
              sx={{
                borderRadius: 4,
                border: "1px solid",
                borderColor: "grey.100",
                boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
                overflow: "hidden",
                bgcolor: "#fff",
              }}
            >
              <Box
                sx={{
                  px: 3,
                  py: 2.5,
                  background: "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Typography variant="subtitle1" fontWeight={800} color="#fff">
                  {t('order_items', { defaultValue: 'ملخص الطلب' })}
                </Typography>
                <Chip
                  label={items.length}
                  size="small"
                  sx={{ bgcolor: "rgba(213,170,47,0.9)", color: "#fff", fontWeight: 800, height: 24 }}
                />
              </Box>

              {items.length === 0 ? (
                <Box sx={{ py: 5, textAlign: "center" }}>
                  <Typography color="text.secondary" fontWeight={600}>
                    {t('no_products')}
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ maxHeight: 380, overflowY: "auto" }}>
                  {items.map((p, idx) => {
                    const imageSrc = p.image ? resolveImageUrl(p.image) : "/placeholder.png";
                    return (
                      <Box
                        key={p.item_id ?? p.product_id ?? idx}
                        sx={{
                          display: "flex",
                          gap: 2,
                          p: 2,
                          borderBottom: "1px solid",
                          borderColor: "grey.50",
                          "&:last-child": { borderBottom: "none" },
                          transition: "bgcolor 0.2s",
                          "&:hover": { bgcolor: "grey.50" },
                        }}
                      >
                        <Avatar
                          variant="rounded"
                          src={imageSrc}
                          alt={getName(p.product) || ""}
                          sx={{ width: 64, height: 64, borderRadius: 2.5, flexShrink: 0 }}
                          onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "/placeholder.png"; }}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            fontWeight={700}
                            sx={{ mb: 0.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                          >
                            {getName(p.product) || `${t('product')} ${p.product_id}`}
                          </Typography>
                          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mb: 0.5 }}>
                            {p.sizeLabel && (
                              <Chip label={p.sizeLabel} size="small" sx={{ height: 18, fontSize: "0.65rem", fontWeight: 600 }} />
                            )}
                            {p.colorLabel && (
                              <Chip label={p.colorLabel} size="small" sx={{ height: 18, fontSize: "0.65rem", fontWeight: 600 }} />
                            )}
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                              ×{p.quantity}
                            </Typography>
                            <Typography variant="body2" fontWeight={800} color="primary.dark">
                              {(Number(p.price || 0) * Number(p.quantity || 1)).toFixed(0)} {lang === "ar" ? "ج.م" : "EGP"}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Paper>

            {/* Price Breakdown */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 4,
                border: "1px solid",
                borderColor: "grey.100",
                boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
                bgcolor: "#fff",
              }}
            >
              <Typography variant="subtitle1" fontWeight={800} mb={2.5}>
                {lang === "ar" ? "تفاصيل السعر" : "Price Details"}
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Typography variant="body2" color="text.secondary" fontWeight={600}>
                    {lang === "ar" ? "المجموع الفرعي" : "Subtotal"}
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>
                    {subtotal.toFixed(2)} {lang === "ar" ? "ج.م" : "EGP"}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <LocalShippingIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                    <Typography variant="body2" color="text.secondary" fontWeight={600}>
                      {lang === "ar" ? "الشحن" : "Shipping"}
                    </Typography>
                  </Box>
                  <Typography variant="body2" fontWeight={700} color={shippingCost > 0 ? "text.primary" : "success.main"}>
                    {shippingCost > 0 ? `${shippingCost.toFixed(2)} ${lang === "ar" ? "ج.م" : "EGP"}` : (lang === "ar" ? "مجاني" : "Free")}
                  </Typography>
                </Box>

                <Divider sx={{ my: 0.5 }} />

                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Typography variant="h6" fontWeight={900}>
                    {lang === "ar" ? "الإجمالي" : "Total"}
                  </Typography>
                  <Box sx={{ textAlign: "end" }}>
                    <Typography
                      variant="h5"
                      fontWeight={900}
                      sx={{
                        background: "linear-gradient(135deg, #d5aa2f 0%, #b08c26 100%)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                      }}
                    >
                      {total.toFixed(2)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" fontWeight={700}>
                      {lang === "ar" ? "ج.م" : "EGP"}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Submit Button (desktop) */}
              <Box sx={{ mt: 3, display: { xs: "none", lg: "flex" }, flexDirection: "column", gap: 1.5 }}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading || paymentLoading || items.length === 0}
                  size="large"
                  fullWidth
                  sx={{
                    py: 1.8,
                    fontSize: "1rem",
                    fontWeight: 900,
                    borderRadius: 3,
                    textTransform: "none",
                    background: "linear-gradient(135deg, #d5aa2f 0%, #e6c76a 100%)",
                    boxShadow: "0 6px 20px rgba(213,170,47,0.4)",
                    "&:hover": { background: "linear-gradient(135deg, #b08c26 0%, #d5aa2f 100%)", boxShadow: "0 8px 28px rgba(213,170,47,0.5)" },
                    "&:disabled": { background: "linear-gradient(135deg, #e0e0e0 0%, #bdbdbd 100%)" },
                  }}
                >
                  {loading || paymentLoading
                    ? (paymentStatus === "pending" ? (lang === "ar" ? "جارٍ تهيئة الدفع..." : "Setting up payment...") : t('loading'))
                    : t('order.create')}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => navigate(-1)}
                  fullWidth
                  sx={{ py: 1.4, fontWeight: 700, borderRadius: 3, textTransform: "none", borderWidth: 2, "&:hover": { borderWidth: 2 } }}
                >
                  {t('cancel')}
                </Button>
              </Box>
            </Paper>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

// read saved country id from localStorage (try several possible keys / shapes)
const savedCountryId = (() => {
  try {
    const maybe = localStorage.getItem("country_id") || localStorage.getItem("selected_country_id");
    if (maybe) return String(maybe);
    const rawUser = localStorage.getItem("user");
    if (rawUser) {
      try {
        const u = JSON.parse(rawUser);
        if (u?.country_id) return String(u.country_id);
        if (u?.country) return String(u.country.id ?? u.country_id ?? "");
      } catch {}
    }
    const rawSel = localStorage.getItem("selected_country");
    if (rawSel) {
      try {
        const sc = JSON.parse(rawSel);
        return String(sc.id ?? sc.country_id ?? sc);
      } catch {}
    }
    return "";
  } catch {
    return "";
  }
})();
